import { supabase } from './supabaseClient';
import { DeliveryMethod, DeliveryStatus, Order, OrderItem, OrderStatus, NotificationType } from '../types';
import { notifyUser } from './notificationService';
import { getOrderStatusLabel } from '../utils/helpers';

import { devError } from '@/utils/devLog';
interface ProcessResult {
    success: boolean;
    status: DeliveryStatus;
    metadata?: any;
    error?: string;
    message?: string;
}

const DISCRETE_ITEMS_METHODS = [
    DeliveryMethod.CODE_INSTANTANE,
    DeliveryMethod.MAIL_CODE,
    DeliveryMethod.MAIL_LICENCE,
    DeliveryMethod.MAIL_COMPTE_CODE,
    DeliveryMethod.MAIL_ACCES
];

const isValidUUID = (uuid: string) => {
    const re = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return re.test(uuid);
};

export const deliveryService = {

    processOrderDelivery: async (order: Order, forceStatus?: OrderStatus): Promise<void> => {
        console.log(`%c [DeliveryService] 🚀 STARTING ORDER #${order.id} `, 'background: #12b8ff; color: #fff; font-weight: bold; padding: 4px; border-radius: 4px;');

        const updatedItems: OrderItem[] = [];
        let allDelivered = true;
        const affectedProductIds = new Set<number>();

        try {
            for (let i = 0; i < order.items.length; i++) {
                const item = order.items[i];
                const method = item.delivery_method || DeliveryMethod.TRAITEMENT_LIEN;

                console.group(`Item ${i}: ${item.nom_produit}`);

                // STRATÉGIE : Les codes instantanés nécessitent la validation de l'administrateur
                // Ils ne sont livrés que lorsque la commande passe à "Terminée" (forceStatus === COMPLETED)
                if (method === DeliveryMethod.CODE_INSTANTANE && forceStatus !== OrderStatus.COMPLETED) {
                    console.log(`%c ⏸️ SKIPPED: CODE_INSTANTANE nécessite validation admin`, 'color: #f59e0b; font-weight: bold;');
                    updatedItems.push(item);
                    if (item.delivery_status !== DeliveryStatus.DELIVERED) allDelivered = false;
                    console.groupEnd();
                    continue;
                }

                if (!item.delivery_status || item.delivery_status === DeliveryStatus.PENDING) {
                    const result = await deliveryService.executeStrategy(order, item, i, method);

                    updatedItems.push({
                        ...item,
                        delivery_status: result.status,
                        delivery_metadata: result.metadata
                    });

                    if (result.status !== DeliveryStatus.DELIVERED) {
                        allDelivered = false;
                        devError(`%c ❌ FAILED: ${result.message || result.error}`, 'color: #ef4444; font-weight: bold;');
                    } else {
                        affectedProductIds.add(item.produit_id);
                        console.log(`%c ✅ SUCCESS`, 'color: #10b981; font-weight: bold;');
                    }

                    // Log technique persistant
                    try {
                        await supabase.from('delivery_logs').insert({
                            order_id: order.id,
                            item_index: i,
                            action: result.success ? 'processed_success' : 'processed_failed',
                            message: result.message || result.error || `Processed via ${method}`,
                            metadata: { technical_error: result.error, pg_message: result.message }
                        });
                    } catch (e) { console.warn("Could not write to delivery_logs", e); }

                } else {
                    updatedItems.push(item);
                    if (item.delivery_status !== DeliveryStatus.DELIVERED) allDelivered = false;
                    console.log(`Already processed as ${item.delivery_status}`);
                }
                console.groupEnd();
            }

            // Si un statut est forcé (par exemple par l'admin), on l'utilise
            let newOrderStatus = forceStatus || order.statut;
            
            // Sinon, on calcule le statut basé sur les items livrés
            if (!forceStatus) {
                const hasProcessing = updatedItems.some(i => i.delivery_status === DeliveryStatus.PROCESSING);
                const hasPhysical = updatedItems.some(i => i.delivery_status === DeliveryStatus.AWAITING_DELIVERY || i.delivery_status === DeliveryStatus.IN_DELIVERY);

                if (allDelivered) newOrderStatus = OrderStatus.COMPLETED;
                else if (hasProcessing || hasPhysical) newOrderStatus = OrderStatus.IN_DELIVERY;
            }

            console.log(`%c [DeliveryService] NEW STATUS: ${newOrderStatus} ${forceStatus ? '(FORCED)' : ''} `, 'background: #333; color: #fff; padding: 2px;');

            // Si un statut est forcé, on ne met à jour que les items, pas le statut de la commande
            // Le statut sera mis à jour par l'appelant (api.ts)
            if (forceStatus) {
                // Mettre à jour uniquement les items
                const { error: itemsError } = await supabase.from('orders').update({ items: updatedItems }).eq('id', order.id);
                if (itemsError) {
                    devError("%c [ITEMS UPDATE ERROR] ", 'background: #ff0000; color: #fff', itemsError);
                } else {
                    console.log("%c [ITEMS UPDATE SUCCESS] ", 'background: #10b981; color: #fff');
                }
            } else {
                // Utilisation d'un RPC pour contourner les restrictions RLS sur la mise à jour du statut par le client
                const { data: rpcResult, error: updateError } = await supabase.rpc('update_order_status_automated', {
                    p_order_id: order.id,
                    p_items: updatedItems,
                    p_new_status: newOrderStatus
                });

                if (updateError) {
                    devError("%c [DB UPDATE ERROR] ", 'background: #ff0000; color: #fff', updateError);
                } else {
                    console.log("%c [DB UPDATE SUCCESS] ", 'background: #10b981; color: #fff', rpcResult);

                    if (rpcResult?.success && newOrderStatus !== order.statut) {
                        // Notifier le client UNIQUEMENT si la base de données a été réellement mise à jour (metadata pour i18n)
                        const statusLabel = getOrderStatusLabel(newOrderStatus);
                        await notifyUser(order.user_id, `Le statut de votre commande #${order.id} est maintenant : ${statusLabel}`, NotificationType.COMMANDE_STATUT, '/dashboard/orders', undefined, { order_id: order.id, order_status: newOrderStatus });
                    } else if (!rpcResult?.success) {
                        console.warn(`%c [DB UPDATE WARNING] La commande #${order.id} n'a pas été trouvée ou mise à jour.`, 'color: #f59e0b');
                    }
                }
            }

            if (affectedProductIds.size > 0) {
                for (const pid of affectedProductIds) {
                    await deliveryService.syncProductStatus(pid);
                }
            }
        } catch (globalErr) {
            devError("%c [CRITICAL GLOBAL ERROR] ", 'background: #ff0000; color: #fff', globalErr);
        }
    },

    executeStrategy: async (order: Order, item: OrderItem, index: number, method: DeliveryMethod): Promise<ProcessResult> => {
        const qty = item.quantite || 1;

        try {
            if (method === DeliveryMethod.TRAITEMENT_LIEN || method === DeliveryMethod.LIVRAISON_DOMICILE) {
                return {
                    success: true,
                    status: method === DeliveryMethod.LIVRAISON_DOMICILE ? DeliveryStatus.AWAITING_DELIVERY : DeliveryStatus.PROCESSING,
                    metadata: { date: new Date().toISOString() }
                };
            }

            if (!order.user_id || !isValidUUID(order.user_id)) {
                return { success: false, status: DeliveryStatus.FAILED, error: 'INVALID_UUID', message: `ID Utilisateur invalide : ${order.user_id}` };
            }

            // IMPORTANT: La RPC process_delivery_item doit réserver un code où product_id = p_product_id
            // et mettre à jour la commande (items[p_item_index]) avec ce code. Sinon un code d'un autre
            // produit peut être assigné à la ligne (ex. code Canva sur ligne Ticket Ooredoo).
            const rpcParams = {
                p_order_id: Math.floor(Number(order.id)),
                p_item_index: Math.floor(Number(index)),
                p_product_id: Math.floor(Number(item.produit_id)),
                p_user_id: order.user_id,
                p_method: String(method)
            };

            const callRpc = async () => {
                console.log(`%c [RPC CALL] process_delivery_item `, 'color: #8b5cf6', rpcParams);
                const { data, error } = await supabase.rpc('process_delivery_item', rpcParams);

                if (error) {
                    throw error;
                }
                return data;
            };

            if (qty > 1 && DISCRETE_ITEMS_METHODS.includes(method)) {
                const results = [];
                for (let k = 0; k < qty; k++) {
                    const rpcResult = await callRpc();
                    if (!rpcResult || !rpcResult.success) {
                        return { success: false, status: DeliveryStatus.FAILED, error: rpcResult?.error || 'FAILURE', message: rpcResult?.message || 'Stock épuisé durant le traitement' };
                    }
                    results.push(rpcResult.data);
                }
                return {
                    success: true,
                    status: DeliveryStatus.DELIVERED,
                    metadata: {
                        quantity: results.length,
                        code_ids: results.map(r => r.code_id),
                        // Capture des références textuelles pour la commande
                        codes: results.map(r => ({ id: r.code_id, ref: r.reference }))
                    }
                };
            } else {
                const rpcResult = await callRpc();
                if (!rpcResult || !rpcResult.success) {
                    return { success: false, status: DeliveryStatus.FAILED, error: rpcResult?.error || 'LOGIC_ERROR', message: rpcResult?.message || 'Le serveur a retourné un échec.' };
                }

                const meta = { ...rpcResult.data };
                // Capture de la référence textuelle unique
                if (rpcResult.data?.code_id && rpcResult.data?.reference) {
                    meta.codes = [{ id: rpcResult.data.code_id, ref: rpcResult.data.reference }];
                }

                return { success: true, status: (rpcResult.status as DeliveryStatus) || DeliveryStatus.DELIVERED, metadata: meta };
            }
        } catch (err: any) {
            return { success: false, status: DeliveryStatus.FAILED, error: err.code || 'UNKNOWN', message: err.message || 'Erreur technique RPC' };
        }
    },

    updatePhysicalStatus: async (orderId: number, itemIndex: number, status: DeliveryStatus, tracking?: string) => {
        const { data: order } = await supabase.from('orders').select('*').eq('id', orderId).single();
        if (!order) return;
        const items = [...(order.items as OrderItem[])];
        if (items[itemIndex]) {
            items[itemIndex].delivery_status = status;
            if (tracking) items[itemIndex].delivery_metadata = { ...(items[itemIndex].delivery_metadata || {}), tracking_number: tracking };
            const allDelivered = items.every(it => it.delivery_status === DeliveryStatus.DELIVERED);
            const newStatus = allDelivered ? OrderStatus.COMPLETED : OrderStatus.IN_DELIVERY;
            await supabase.from('orders').update({ items, statut: newStatus }).eq('id', orderId);

            if (newStatus !== order.statut) {
                const statusLabel = getOrderStatusLabel(newStatus);
                await notifyUser(order.user_id, `Le statut de votre commande #${orderId} est maintenant : ${statusLabel}`, NotificationType.COMMANDE_STATUT, '/dashboard/orders', undefined, { order_id: orderId, order_status: newStatus });
            }
        }
    },

    syncProductStatus: async (productId: number) => {
        const { error } = await supabase.rpc('sync_product_stock_status', { p_product_id: Math.floor(Number(productId)) });
        if (error) {
            console.warn("Sync failed", error);
        }
    }
};
